import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { Minus, Plus, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CartDrawer = () => {
  const { items, open, setOpen, removeItem, updateQty, subtotal, shipping, total } = useCart();
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="bg-[#F5F0EB] border-l border-[#dcd7ce] w-full sm:max-w-md p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#dcd7ce]">
          <SheetTitle className="font-serif-display text-2xl text-[#1A1A1A] text-left">
            {t.cart.title}
          </SheetTitle>
          <SheetDescription className="sr-only">{t.cart.title}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#4a4a4a] mb-6">{t.cart.empty}</p>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-empty-cta"
                className="text-sm tracking-[0.18em] uppercase underline underline-offset-4 hover:opacity-60"
              >
                {t.cart.emptyCta}
              </button>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map((item) => (
                <li
                  key={item.key}
                  data-testid={`cart-item-${item.key}`}
                  className="flex gap-4"
                >
                  <div className="w-20 h-24 bg-[#ebe7df] rounded-sm overflow-hidden flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.product_name[lang]}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-serif-display text-lg leading-tight">
                          {item.product_name[lang]}
                        </div>
                        <div className="text-xs text-[#4a4a4a] mt-1">
                          {item.variant_name[lang]}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.key)}
                        data-testid={`cart-remove-${item.key}`}
                        aria-label={t.cart.remove}
                        className="text-[#4a4a4a] hover:text-[#1A1A1A]"
                      >
                        <X size={16} strokeWidth={1.4} />
                      </button>
                    </div>
                    <div className="text-sm text-[#4a4a4a] mt-2">
                      {item.price.toFixed(2)} {t.common.currency}
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => updateQty(item.key, item.quantity - 1)}
                        data-testid={`cart-decrement-${item.key}`}
                        className="w-7 h-7 border border-[#dcd7ce] hover:border-[#1A1A1A] flex items-center justify-center"
                      >
                        <Minus size={12} strokeWidth={1.4} />
                      </button>
                      <span
                        className="text-sm w-6 text-center"
                        data-testid={`cart-qty-${item.key}`}
                      >
                        {item.quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateQty(item.key, item.quantity + 1)}
                        data-testid={`cart-increment-${item.key}`}
                        className="w-7 h-7 border border-[#dcd7ce] hover:border-[#1A1A1A] flex items-center justify-center"
                      >
                        <Plus size={12} strokeWidth={1.4} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#dcd7ce] px-6 py-6 space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#4a4a4a]">{t.cart.subtotal}</span>
              <span data-testid="cart-subtotal">
                {subtotal.toFixed(2)} {t.common.currency}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#4a4a4a]">{t.cart.shipping}</span>
              <span data-testid="cart-shipping">{t.cart.shippingFree}</span>
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-[#dcd7ce]">
              <span className="font-serif-display text-lg">{t.cart.total}</span>
              <span
                data-testid="cart-total"
                className="font-serif-display text-lg"
              >
                {total.toFixed(2)} {t.common.currency}
              </span>
            </div>
            <button
              type="button"
              onClick={goCheckout}
              data-testid="cart-checkout-button"
              className="w-full bg-[#3D2B1F] text-white py-4 text-sm tracking-[0.22em] uppercase font-medium hover:bg-[#1A1A1A] hover:-translate-y-0.5 transition-all duration-300 rounded-md shadow-[0_8px_24px_-10px_rgba(61,43,31,0.5)]"
            >
              {t.cart.checkout}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
